import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, Fragment, useEffect, useLayoutEffect, classNames, useAsObservableSource, Suspense, useHotkeys, SplitterLayout} from '@api'
import {CatalogRoot, LeftSidebar, LoadingIndicator, RightSidebar, StoresInstanceContext, TopBar, useDashboardContentStore, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {SeasonSelectorToolbar, StoresInstance} from '@stores'
import * as css from './CatalogDashboardPage.css'

/// region Store

export class CatalogDashboardPageStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className' | 'storesToInject'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }
}

export const CatalogDashboardPageStoreContext = React.createContext<CatalogDashboardPageStore>(null)
export const useCatalogDashboardPageStore = () => React.useContext(CatalogDashboardPageStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  storesToInject?: StoresInstance
}

export const CatalogDashboardPage = observer<MyProps>(function CatalogDashboardPage({className, storesToInject, ...props}) {
  const {props: {gridRef}} = useDashboardContentStore(),
    obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new CatalogDashboardPageStore(storesToInject, obsProps)),
    {stores: {flexLayout, flexLayout: {isMaximized, activeTab}}} = store,
    {
      sidebar: {right},
      workspaces: {view},
      catalog, catalog: {isShowingClientPDF}
    } = storesToInject

  useEffect(() => store.registerEffects(), [])

  const activeCatalog = flexLayout?.activeCatalogRoot?.catalogRoot?.catalog

  useHotkeys('ctrl+space,cmd+space', e => {
    if (activeTab?.component === 'whiteboard') return
    e.preventDefault()
    e.stopPropagation()
    activeCatalog?.searcher?.focusAndOpenSearcher()
  }, [activeCatalog, activeTab])

  useHotkeys('ctrl+a,cmd+a', e => {
    if (activeTab?.component === 'whiteboard') return
    e.preventDefault()
    e.stopPropagation()
    activeCatalog?.onSelectAll(e)
  }, [activeCatalog, activeTab])

  // useHotkeys('ctrl+s,cmd+s', (e) => {
  //   if (activeTab?.component === 'whiteboard') return
  //   e.preventDefault()
  //   e.stopPropagation()
  //   activeCatalog?.presets.saveCurrentPreset()
  // }, [activeCatalog, activeTab])

  return (
    <CatalogDashboardPageStoreContext.Provider value={store}>
      <Fragment>
        <div className={css.grid} ref={gridRef}>
          <div className={classNames(css.rightPanel)}>
            <div className={css.topBar}>
              {isMaximized && <StoresInstanceContext.Provider value={storesToInject}><SeasonSelectorToolbar/></StoresInstanceContext.Provider>}
              <TopBar catalog={catalog}/>
            </div>
            <DashboardSplitterPanel/>
          </div>
        </div>
        {isMaximized && activeTab.component != 'inlineCatalogItemDetails'  /*|| (right.open && right.type === 'modal')) */ && <StoresInstanceContext.Provider value={storesToInject}>
	        <Suspense fallback={null}>
              {isShowingClientPDF && right.open && view !== 'library' ? null /** We show it inline instead {@see CatalogRootFlexLayoutStore.children} **/
                                                                      : right.open || catalog.aggregation?.isShowingAggregateDetails ? <RightSidebar isGlobalSidebar/> : null}
	        </Suspense>
        </StoresInstanceContext.Provider>}
      </Fragment>
    </CatalogDashboardPageStoreContext.Provider>)
})

/// endregion

export const DashboardSplitterPanel = observer(function DashboardSplitterPanel() {
  const stores = useStores(),
    {flexLayout} = stores,
    storesToInject: StoresInstance = flexLayout?.maximizedTab?.storesToInject ?? stores,
    {sidebar: {left}} = storesToInject

  // if (storesToInject.isGlobalStore) {
  //   return <DashboardDetailPanelContent key='dashboard-details-panel'/>
  // }

  // useEffect(() => {
  //   console.log(`Mount DashboardSplitterPanel`)
  //   return () => {
  //     console.log(`Unmount DashboardSplitterPanel`)
  //   }
  // }, [])

  if (!flexLayout?.maximizedTab) return <DashboardDetailPanelContent key='dashboard-details-panel'/>  // Splitter is expensive to keep around as it measures on resize and impacts perf

  return (
    <SplitterLayout
      ref={left.setSplitterComponent}
      split='vertical'
      customClassName={classNames(css.splitter, flexLayout?.maximizedTab?.component === 'catalog' && left.open && css.open, !flexLayout.model && css.hidden)}
      secondaryInitialSize={left.initialWidth}
      primaryIndex={1}
      secondaryMinSize={200}
      primaryMinSize={250}
      onSecondaryPaneSizeChange={left.onSizeChange}
    >
      <StoresInstanceContext.Provider value={storesToInject}>
        <LeftSidebar key='left-sidebar'/>
      </StoresInstanceContext.Provider>
      <DashboardDetailPanelContent key='dashboard-details-panel'/>
    </SplitterLayout>
  )
})

/**
 * Show within the right pane of the legacy left sidebar <-> catalog pane L&F and by itself in a flex layout
 */
const DashboardDetailPanelContent = observer(function DashboardDetailPanelContent() {
  const {
    reports: {currentReport}
  } = useStores()

  // useEffect(() => {
  //   console.log(`Mount DashboardDetailPanelContent`)
  //   return () => console.log(`Unmount DashboardDetailPanelContent`)
  // })

  return !currentReport ? <LoadingIndicator/> : <CatalogRoot maximizedRoot/>
})



// WEBPACK FOOTER //
// ./src/modules/catalog/pages/CatalogDashboardPage.tsx